/**
 * Member activity page — `/users/:sourceUserId` (WSRADM-0044 / WSRADM-0045).
 *
 * Header from `GET /admin/users/{id}`; tabs Sales / Check-ins filter by
 * `memberId` (= `sourceUserId`). A sale's `sourceClientId` IS that id;
 * `WarehouseUser.clientId` is an unrelated WStudio VARCHAR — never a join key.
 * Attendance matching is the server's answer — never de-duplicate client-side.
 * Product cells link when `productMatch === 'MATCHED'` (WSRADM-0045).
 */
import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/api/reports/format';
import { getUser, listAttendance, listSales } from '@/api/warehouse/warehouseApi';
import type {
  NameMatch,
  WarehouseAttendance,
  WarehouseSale,
  WarehouseUser,
} from '@/api/warehouse/types';
import { ErrorDetails } from '@/components/ErrorDetails';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import {
  EM_DASH,
  boolLabel,
  formatWarehouseDateTime,
  textOrDash,
} from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

type MemberTab = 'sales' | 'checkins';

function parseTab(raw: string | null): MemberTab {
  return raw === 'checkins' ? 'checkins' : 'sales';
}

function parseNonNegInt(raw: string | null, fallback: number): number {
  if (raw === null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.floor(n);
}

/** Positive integer path segment only — rejects `812abc`, negatives, empties. */
function parseSourceUserId(raw: string | undefined): number | null {
  if (raw === undefined || raw === '') return null;
  if (!/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

function tabDefaults(tab: MemberTab): { sort: string; order: 'asc' | 'desc' } {
  return tab === 'checkins'
    ? { sort: 'attendedOn', order: 'desc' }
    : { sort: 'saleDate', order: 'desc' };
}

function HeaderRow({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      <Box component="span" sx={{ color: 'text.primary', mr: 1 }}>
        {label}:
      </Box>
      {value}
    </Typography>
  );
}

function productMatchTitle(
  match: NameMatch,
  t: (key: string, fallback?: string) => string,
): string | undefined {
  if (match === 'AMBIGUOUS') {
    return t(
      'warehouse.attendance.matchAmbiguous',
      "Several members share this name, so this row can't be linked to one.",
    );
  }
  if (match === 'UNMATCHED') {
    return t('warehouse.attendance.matchUnmatched', 'No member matches this name.');
  }
  return undefined;
}

function ProductNameCell({
  row,
  t,
}: {
  row: WarehouseAttendance;
  t: (key: string, fallback?: string) => string;
}) {
  if (row.productMatch === 'NONE') {
    return EM_DASH;
  }
  const label = textOrDash(row.productName);
  if (row.productMatch === 'MATCHED' && row.sourceProductId !== null) {
    return (
      <Link component={RouterLink} to={`/products/${row.sourceProductId}`}>
        {label}
      </Link>
    );
  }
  if (row.productMatch === 'AMBIGUOUS' || row.productMatch === 'UNMATCHED') {
    const title = productMatchTitle(row.productMatch, t);
    return title !== undefined ? <span title={title}>{label}</span> : label;
  }
  return label;
}

function MemberHeader({
  user,
  yes,
  no,
  t,
}: {
  user: WarehouseUser;
  yes: string;
  no: string;
  t: (key: string, fallback?: string) => string;
}) {
  const title =
    user.name !== null && user.name.length > 0 ? user.name : `#${user.sourceUserId}`;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <HeaderRow label={t('users.columns.email', 'Email')} value={textOrDash(user.email)} />
      <HeaderRow label={t('users.columns.phone', 'Phone')} value={textOrDash(user.phone)} />
      <HeaderRow
        label={t('warehouse.member.clientId', 'WStudio client id')}
        value={textOrDash(user.clientId)}
      />
      <HeaderRow label={t('warehouse.member.created', 'Created')} value={textOrDash(user.dateCreated)} />
      <HeaderRow
        label={t('warehouse.member.statusActive', 'Membership active')}
        value={boolLabel(user.statusIsActive, yes, no)}
      />
      <HeaderRow
        label={t('warehouse.member.statusExpiration', 'Status expiration')}
        value={textOrDash(user.statusExpirationDate)}
      />
      <HeaderRow
        label={t('warehouse.member.statusOnHold', 'On hold')}
        value={boolLabel(user.statusIsOnHold, yes, no)}
      />
      <HeaderRow
        label={t('warehouse.member.statusBlocked', 'Blocked date')}
        value={textOrDash(user.statusBlockedDate)}
      />
      <HeaderRow
        label={t('warehouse.member.statusLastVisit', 'Last visit')}
        value={textOrDash(user.statusLastVisit)}
      />
      <HeaderRow
        label={t('warehouse.member.accountEnabled', 'WStudio account enabled')}
        value={boolLabel(user.isActive, yes, no)}
      />
    </Paper>
  );
}

export function MemberPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const { sourceUserId: rawId } = useParams<{ sourceUserId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = parseSourceUserId(rawId);

  const tab = parseTab(searchParams.get('tab'));
  const defaults = tabDefaults(tab);
  const page = parseNonNegInt(searchParams.get('page'), 0);
  const size = parseNonNegInt(searchParams.get('size'), PAGE_SIZE_DEFAULT);
  const sort = searchParams.get('sort') ?? defaults.sort;
  const orderRaw = searchParams.get('order');
  const order: 'asc' | 'desc' =
    orderRaw === 'asc' || orderRaw === 'desc' ? orderRaw : defaults.order;

  const yes = t('common.yes', 'Yes');
  const no = t('common.no', 'No');

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  };

  const setTab = (next: MemberTab) => {
    patchParams({
      tab: next === 'sales' ? null : next,
      page: null,
      sort: null,
      order: null,
    });
  };

  const userRes = useResource(() => {
    // Invalid path: never hit the network (not-found panel renders instead).
    if (id === null) return Promise.reject(new Error('invalid-id'));
    return getUser(id);
  }, [id]);

  const fallbackTitle = t('warehouse.member.title', 'Member');
  const docTitle =
    userRes.status === 'ready' && userRes.data !== null
      ? userRes.data.name !== null && userRes.data.name.length > 0
        ? userRes.data.name
        : `#${userRes.data.sourceUserId}`
      : fallbackTitle;
  useDocumentTitle(docTitle);

  const salesRes = useResource(() => {
    if (id === null || tab !== 'sales') {
      return Promise.resolve({
        content: [] as WarehouseSale[],
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
      });
    }
    return listSales({
      memberId: id,
      page,
      size,
      sort,
      order,
      search: '',
      includeVoids: false,
    });
  }, [id, tab, page, size, sort, order]);

  const checkinsRes = useResource(() => {
    if (id === null || tab !== 'checkins') {
      return Promise.resolve({
        content: [] as WarehouseAttendance[],
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
        memberFilterMatch: null,
        memberFilterSharedWith: null,
        productFilterMatch: null,
        productFilterSharedWith: null,
      });
    }
    return listAttendance({
      memberId: id,
      page,
      size,
      sort,
      order,
      search: '',
    });
  }, [id, tab, page, size, sort, order]);

  const salesColumns = useMemo<RosterColumn<WarehouseSale>[]>(
    () => [
      {
        id: 'saleDate',
        header: t('sales.columns.saleDate', 'Sale date'),
        sortId: 'saleDate',
        defaultOrder: 'desc',
        render: (row) => formatWarehouseDateTime(row.saleDate, i18n.language),
      },
      {
        id: 'documentNumber',
        header: t('sales.columns.documentNumber', 'Document'),
        sortId: 'documentNumber',
        render: (row) => textOrDash(row.documentNumber),
      },
      {
        id: 'total',
        header: t('sales.columns.total', 'Total'),
        sortId: 'total',
        render: (row) =>
          row.total === null
            ? EM_DASH
            : formatMoney(row.total, row.currency ?? '', i18n.language),
      },
      {
        id: 'paymentAccount',
        header: t('sales.columns.paymentAccount', 'Payment account'),
        render: (row) => textOrDash(row.paymentAccountName),
      },
      {
        id: 'deleted',
        header: t('sales.columns.deleted', 'Voided'),
        render: (row) => boolLabel(row.deleted, yes, no),
      },
    ],
    [i18n.language, no, t, yes],
  );

  const checkinsColumns = useMemo<RosterColumn<WarehouseAttendance>[]>(
    () => [
      {
        id: 'attendedOn',
        header: t('attendance.columns.attendedOn', 'Date'),
        sortId: 'attendedOn',
        defaultOrder: 'desc',
        render: (row) => row.attendedOn,
      },
      {
        id: 'attendedAtTime',
        header: t('attendance.columns.attendedAtTime', 'Time'),
        render: (row) => textOrDash(row.attendedAtTime),
      },
      {
        id: 'className',
        header: t('attendance.columns.className', 'Class'),
        sortId: 'className',
        render: (row) => textOrDash(row.className),
      },
      {
        id: 'productName',
        header: t('attendance.columns.productName', 'Product'),
        sortId: 'productName',
        render: (row) => <ProductNameCell row={row} t={t} />,
      },
      {
        id: 'instructor',
        header: t('attendance.columns.instructor', 'Instructor'),
        sortId: 'instructor',
        render: (row) => textOrDash(row.instructor),
      },
    ],
    [t],
  );

  if (id === null) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('warehouse.member.notFound', 'That member was not found.')}
        </Alert>
        <Button component={RouterLink} to="/users" variant="text">
          {t('warehouse.member.back', 'Back to users')}
        </Button>
      </Box>
    );
  }

  const sharedWith = checkinsRes.data?.memberFilterSharedWith;
  const showSharedCaption =
    tab === 'checkins' &&
    checkinsRes.data?.memberFilterMatch === 'AMBIGUOUS' &&
    sharedWith !== null &&
    sharedWith !== undefined;

  return (
    <Box>
      <Button component={RouterLink} to="/users" variant="text" sx={{ mb: 1 }}>
        {t('warehouse.member.back', 'Back to users')}
      </Button>

      {userRes.status === 'loading' ? (
        <Skeleton variant="rounded" height={200} role="status" sx={{ mb: 3 }} />
      ) : null}
      {userRes.status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={userRes.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('roster.loadError', 'Couldn’t load this list.')}
          <ErrorDetails
            intent="read"
            error={userRes.error}
            headline={t('roster.loadError', 'Couldn’t load this list.')}
          />
        </Alert>
      ) : null}
      {userRes.data !== null ? (
        <MemberHeader user={userRes.data} yes={yes} no={no} t={t} />
      ) : null}

      <Tabs
        value={tab}
        onChange={(_event, value: MemberTab) => setTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab value="sales" label={t('warehouse.member.tabSales', 'Sales transactions')} />
        <Tab value="checkins" label={t('warehouse.member.tabCheckins', 'Check-ins')} />
      </Tabs>

      {tab === 'sales' ? (
        <RosterTable
          title={t('warehouse.member.tabSales', 'Sales transactions')}
          columns={salesColumns}
          rows={salesRes.data?.content ?? []}
          rowId={(row) => String(row.sourceSaleId)}
          status={salesRes.status}
          error={salesRes.error}
          totalElements={salesRes.data?.totalElements ?? 0}
          page={page}
          size={size}
          sort={sort}
          order={order}
          onPageChange={(next) => patchParams({ page: next === 0 ? null : String(next) })}
          onRowsPerPageChange={(next) =>
            patchParams({
              size: next === PAGE_SIZE_DEFAULT ? null : String(next),
              page: null,
            })
          }
          onSortChange={(idKey, nextOrder) => {
            patchParams({
              sort: idKey,
              order: nextOrder,
              page: null,
            });
          }}
          onApply={() => {
            /* member tabs keep search empty */
          }}
          onRetry={salesRes.refetch}
        />
      ) : (
        <>
          {showSharedCaption ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'warehouse.member.sharedName',
                '{{n}} members share this name; these check-ins may include theirs.',
                { n: sharedWith },
              )}
            </Alert>
          ) : null}
          <RosterTable
            title={t('warehouse.member.tabCheckins', 'Check-ins')}
            columns={checkinsColumns}
            rows={checkinsRes.data?.content ?? []}
            rowId={(row) => row.naturalKeyHash}
            status={checkinsRes.status}
            error={checkinsRes.error}
            totalElements={checkinsRes.data?.totalElements ?? 0}
            page={page}
            size={size}
            sort={sort}
            order={order}
            onPageChange={(next) => patchParams({ page: next === 0 ? null : String(next) })}
            onRowsPerPageChange={(next) =>
              patchParams({
                size: next === PAGE_SIZE_DEFAULT ? null : String(next),
                page: null,
              })
            }
            onSortChange={(idKey, nextOrder) => {
              patchParams({
                sort: idKey,
                order: nextOrder,
                page: null,
              });
            }}
            onApply={() => {
              /* member tabs keep search empty */
            }}
            onRetry={checkinsRes.refetch}
          />
        </>
      )}
    </Box>
  );
}
